import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { createExpenseSchema, type CreateExpenseData } from "@/schemas/admin/AdminExpensesTab/createExpenseSchema";
import { useToast } from "@/hooks/use-toast";
import { isContractActive } from "@/utils/contractUtils";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";



import FormModal from "@/components/v2/molecules/admin/shared/FormModal";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useExpenseCategories,
  useExpenseSubcategories,
} from "@/hooks/data/useEntityCategories";

import {
  useCreateExpense,
  useUpdateExpense,
  type Expense,
} from "@/hooks/useExpenses";

import { useProperties } from "@/hooks/useProperties";
import { uploadReceiptToStorage } from "@/utils/fileUpload";
import { format } from "date-fns";
import { Check, ChevronsUpDown, Plus, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { PAYMENT_METHODS } from "@/constants/expenses";
import { useContracts } from "@/hooks/useContracts";
import { supabase } from "@/integrations/supabase/client";
import { useExpenseTags, useCreateExpenseTag } from "@/hooks/useExpenseTags";

interface ExpenseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense?: Expense | null;
  defaultPropertyId?: string;
}

const ExpenseFormModal = ({
  isOpen,
  onClose,
  expense,
  defaultPropertyId,
}: ExpenseFormModalProps) => {
  const { data: properties = [] } = useProperties();
  const { data: contracts = [] } = useContracts();
  const createExpenseMutation = useCreateExpense();
  const updateExpenseMutation = useUpdateExpense();
  const { toast } = useToast();

  const form = useForm<CreateExpenseData>({
    resolver: zodResolver(createExpenseSchema),
    defaultValues: {
      expense_date: format(new Date(), "yyyy-MM-dd"),
      property_id: "",
      category_id: "",
      subcategory_id: "",
      description: "",
      payment_method: "",
      status: "Pagado",
      receipt_url: "",
      amount: "",
      tag_id: "",
    },
  });

  const watchCategoryId = form.watch("category_id");
  const watchPropertyId = form.watch("property_id");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [propertyPopoverOpen, setPropertyPopoverOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: "", color: "#3B82F6" });

  const { data: tags = [] } = useExpenseTags();
  const createTagMutation = useCreateExpenseTag();

  const defaultColors = [
    "#3B82F6", // blue
    "#EF4444", // red
    "#10B981", // green
    "#F59E0B", // amber
    "#8B5CF6", // purple
    "#EC4899", // pink
    "#14B8A6", // teal
    "#F97316", // orange
    "#D2B48C", // beige/arena
  ];

  const { data: categories = [] } = useExpenseCategories();
  const { data: subcategories = [], isLoading: isLoadingSubcategories } = useExpenseSubcategories(
    watchCategoryId,
  );

  // Inicializar el formulario con los datos del gasto
  useEffect(() => {
    if (expense) {
      form.reset({
        expense_date: expense.expense_date,
        property_id: expense.property_id,
        category_id: expense.category_id,
        subcategory_id: "",
        description: expense.description || "",
        payment_method: expense.payment_method,
        status: expense.status,
        receipt_url: expense.receipt_url || "",
        amount: expense.amount.toString(),
        tag_id: expense.tag_id || "",
      });
    } else {
      form.reset({
        expense_date: format(new Date(), "yyyy-MM-dd"),
        property_id: defaultPropertyId || "",
        category_id: "",
        subcategory_id: "",
        description: "",
        payment_method: "",
        status: "Pagado",
        receipt_url: "",
        amount: "",
        tag_id: "",
      });
    }
    setSelectedFile(null);
  }, [expense, isOpen, defaultPropertyId, form]);

  // Establecer subcategory_id una vez que las subcategorías se hayan cargado
  useEffect(() => {
    if (expense && expense.subcategory_id && !isLoadingSubcategories && subcategories.length > 0) {
      const subcategoryExists = subcategories.find(sub => sub.id === expense.subcategory_id);
      if (subcategoryExists) {
        form.setValue("subcategory_id", expense.subcategory_id);
      }
    }
  }, [expense, subcategories, isLoadingSubcategories, form]);

  useEffect(() => {
    const currentSubcategoryId = form.watch("subcategory_id");
    if (watchCategoryId && currentSubcategoryId) {
      const currentSubcategory = subcategories.find(
        (sub) => sub.id === currentSubcategoryId,
      );
      if (
        !currentSubcategory ||
        currentSubcategory.category_id !== watchCategoryId
      ) {
        form.setValue("subcategory_id", "");
      }
    }
  }, [watchCategoryId, subcategories, form]);

  const handlePropertySelect = (propertyId: string) => {
    form.setValue("property_id", propertyId);
    setPropertyPopoverOpen(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const onSubmit = async (data: CreateExpenseData) => {
    setUploading(true);

    try {
      let receiptUrl = data.receipt_url;

      if (selectedFile) {
        receiptUrl = await uploadReceiptToStorage(selectedFile);
      }

      const expenseData = {
        expense_date: data.expense_date,
        property_id: data.property_id,
        category_id: data.category_id,
        subcategory_id: data.subcategory_id,
        description: data.description,
        amount: parseFloat(data.amount),
        payment_method: data.payment_method,
        status: data.status,
        receipt_url: receiptUrl || undefined,
        tag_id: data.tag_id || undefined,
      };

      if (expense) {
        await updateExpenseMutation.mutateAsync({
          id: expense.id,
          ...expenseData,
        });
        toast({
          title: "Éxito",
          description: "Gasto actualizado correctamente",
        });
      } else {
        await createExpenseMutation.mutateAsync(expenseData);
        toast({
          title: "Éxito",
          description: "Gasto creado correctamente",
        });
      }

      onClose();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al guardar el gasto",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    form.setValue("receipt_url", "");
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;

    try {
      const createdTag = await createTagMutation.mutateAsync(newTag);
      form.setValue("tag_id", createdTag.id);
      setNewTag({ name: "", color: "#3B82F6" });
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const selectedProperty = properties.find((p) => p.id === watchPropertyId);
  const selectedPropertyContract = contracts.find((c) =>
    c.property_id === watchPropertyId && isContractActive(c)
  );

  const getCurrentMonthName = () => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return months[new Date().getMonth()];
  };

  const getPreviousMonthName = () => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const now = new Date();
    const previousMonth = now.getMonth() - 1;
    return previousMonth < 0 ? months[11] : months[previousMonth];
  };

  const calculatePreviousMonthIncome = async (propertyId: string) => {
    try {
      const now = new Date();
      const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const startDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);
      const endDate = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0);

      // Obtener bookings de la propiedad
      const { data: bookings } = await supabase
        .from('bookings')
        .select('booking_id')
        .eq('property_id', propertyId);

      if (!bookings || bookings.length === 0) return 0;

      const bookingIds = bookings.map(b => b.booking_id);

      // Obtener ingresos del mes anterior usando la misma lógica del dashboard
      const { data: incomes } = await supabase
        .from('property_income')
        .select('payout')
        .in('booking_id', bookingIds)
        .gte('payout_date', startDate.toISOString().split('T')[0])
        .lte('payout_date', endDate.toISOString().split('T')[0]);

      return incomes?.reduce((sum, income) => sum + income.payout, 0) || 0;
    } catch (error) {
      console.error('Error calculating previous month income:', error);
      return 0;
    }
  };

  const handleRecurringExpenseSelect = async (type: 'limpieza' | 'internet' | 'gas' | 'mantenimiento' | 'cuota-admin' | 'comision-admin') => {
    if (!watchPropertyId || !categories.length) return;

    let expenseData = {
      payment_method: "",
      description: "",
      amount: "",
      category_id: "",
    };

    // Buscar la categoría apropiada y obtener sus subcategorías
    let targetCategory;
    let subcategoryName = "";

    switch (type) {
      case 'limpieza':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('limpieza'));
        subcategoryName = "servicio de limpieza";
        expenseData = {
          ...expenseData,
          amount: selectedPropertyContract?.cleaning_rate?.toString() || "",
          description: "Limpieza EC",
          payment_method: "Sin definir",
        };
        break;

      case 'internet':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('servicios'));
        subcategoryName = "internet";
        expenseData = {
          ...expenseData,
          description: `Pago de internet - ${getCurrentMonthName()}`,
          payment_method: "Transferencia",
        };
        break;

      case 'gas':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('servicios'));
        subcategoryName = "gas";
        expenseData = {
          ...expenseData,
          description: "Pago de Gas LP",
          payment_method: "Transferencia",
        };
        break;

      case 'mantenimiento':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('servicios'));
        subcategoryName = "mantenimiento";
        expenseData = {
          ...expenseData,
          description: `Pago de mantenimiento - ${getCurrentMonthName()}`,
          payment_method: "Transferencia",
        };
        break;

      case 'cuota-admin':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('administración'));
        subcategoryName = "cuota de administración";
        expenseData = {
          ...expenseData,
          amount: selectedPropertyContract?.administration_rate?.toString() || "",
          description: `Pago de administración ${getPreviousMonthName()}`,
          payment_method: "Transferencia",
        };
        break;

      case 'comision-admin':
        targetCategory = categories.find(c => c.name.toLowerCase().includes('administración'));
        subcategoryName = "comisión de administración";
        const previousMonthIncome = await calculatePreviousMonthIncome(watchPropertyId);
        const commissionRate = selectedPropertyContract?.administration_commission || 0;
        const commissionAmount = (commissionRate / 100) * previousMonthIncome;

        expenseData = {
          ...expenseData,
          amount: commissionAmount.toString(),
          description: `${commissionRate}% de $${previousMonthIncome.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
          payment_method: "Transferencia",
        };
        break;
    }

    if (!targetCategory) return;

    // Primero establecer la categoría
    form.setValue("payment_method", expenseData.payment_method);
    form.setValue("description", expenseData.description);
    form.setValue("amount", expenseData.amount);
    form.setValue("category_id", targetCategory.id);
    form.setValue("subcategory_id", "");

    // Esperar un momento y luego buscar la subcategoría
    setTimeout(async () => {
      try {
        const { data: categorySubcategories } = await supabase
          .from('properties_expenses_subcategories')
          .select('*')
          .eq('category_id', targetCategory.id);

        const matchingSubcategory = categorySubcategories?.find(s =>
          s.name.toLowerCase().includes(subcategoryName.toLowerCase())
        );

        if (matchingSubcategory) {
          form.setValue("subcategory_id", matchingSubcategory.id);
        }
      } catch (error) {
        console.error('Error finding subcategory:', error);
      }
    }, 100);

    // Set status to "Pagado" for recurring expenses
    setTimeout(() => {
      form.setValue("status", "Pagado");
    }, 150);
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={expense ? "Editar Gasto" : "Nuevo Gasto"}
      form={form}
      onSubmit={onSubmit}
      isLoading={uploading || createExpenseMutation.isPending || updateExpenseMutation.isPending}
      submitLabel={uploading ? "Subiendo..." : expense ? "Actualizar Gasto" : "Crear Gasto"}
      maxWidth="max-w-2xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="expense_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Fecha de Gasto *</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  {...field}
                  className="mt-1"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="property_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Propiedad *</FormLabel>
              <Popover
                open={propertyPopoverOpen}
                onOpenChange={setPropertyPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between mt-1"
                    >
                      {field.value
                        ? properties.find((p) => p.id === field.value)?.shortName ||
                        properties.find((p) => p.id === field.value)?.title
                        : "Buscar y seleccionar propiedad..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar propiedad..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron propiedades.</CommandEmpty>
                      <CommandGroup>
                        {properties.map((property) => (
                          <CommandItem
                            key={property.id}
                            value={`${property.shortName || property.title} ${property.id
                              }`}
                            onSelect={() => {
                              field.onChange(property.id);
                              setPropertyPopoverOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${field.value === property.id
                                ? "opacity-100"
                                : "opacity-0"
                                }`}
                            />
                            {property.shortName || property.title}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {watchPropertyId && !expense && (
        <div>
          <Label>Gastos Recurrentes</Label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('limpieza')}
            >
              Limpieza
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('internet')}
            >
              Internet
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('gas')}
            >
              Gas
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('mantenimiento')}
            >
              Mantenimiento
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('cuota-admin')}
            >
              Cuota Admin
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => handleRecurringExpenseSelect('comision-admin')}
            >
              Comisión Admin
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Monto *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...field}
                  className="mt-1"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payment_method"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Forma de Pago *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar forma de pago" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="category_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoría *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="subcategory_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subcategoría *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={!watchCategoryId}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar subcategoría" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {subcategories.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Estatus *</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estatus" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Pagado">Pagado</SelectItem>
                <SelectItem value="Programado">Programado</SelectItem>
                <SelectItem value="Pendiente">Pendiente</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="tag_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Etiqueta</FormLabel>
            <div className="flex gap-2 mt-1">
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Seleccionar etiqueta (opcional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10">
                    <Plus className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="new-tag-name" className="text-sm">
                        Nueva etiqueta
                      </Label>
                      <Input
                        id="new-tag-name"
                        value={newTag.name}
                        onChange={(e) =>
                          setNewTag((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Nombre..."
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-sm">Color</Label>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {defaultColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full border-2 transition-transform ${newTag.color === color
                              ? "border-gray-900 scale-110"
                              : "border-transparent"
                              }`}
                            style={{ backgroundColor: color }}
                            onClick={() =>
                              setNewTag((prev) => ({ ...prev, color }))
                            }
                          />
                        ))}
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateTag}
                      disabled={!newTag.name.trim() || createTagMutation.isPending}
                      className="w-full"
                    >
                      {createTagMutation.isPending ? "Creando..." : "Crear"}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Descripción</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                placeholder="Descripción corta del gasto..."
                maxLength={70}
                rows={1}
                className="resize-none min-h-[40px] mt-1"
              />
            </FormControl>
            <div className="text-sm text-muted-foreground mt-1">
              {field.value?.length || 0}/70 caracteres
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <div>
        <Label>Comprobante</Label>
        <div className="mt-2">
          {selectedFile || form.watch("receipt_url") ? (
            <div className="flex items-center justify-between p-2 border rounded-md">
              <span className="text-sm text-gray-600 truncate">
                {selectedFile ? selectedFile.name : "Comprobante actual"}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removeFile}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-md p-4">
              <label htmlFor="receipt" className="cursor-pointer flex items-center gap-3">
                <Upload className="h-8 w-8 text-gray-400 flex-shrink-0" />
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 block">
                    Subir comprobante
                  </span>
                  <span className="text-xs text-gray-500">
                    JPG, PNG, PDF hasta 10MB
                  </span>
                </div>
              </label>
              <input
                id="receipt"
                type="file"
                className="sr-only"
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
              />
            </div>
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default ExpenseFormModal;